/* eslint-disable max-lines */
import { useAlarmTypeGroups } from '@hakimo-ui/hakimo/data-access';
import { AlarmTypeGroup, AlarmTypeGroupFilters } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { Pagination, Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  useCanAddAlarmTypeGroup,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import AddEditAlarmTypeGroup from '../add-alarm-type-group/AddEditAlarmTypeGroup';
import AddAlarmTypesToGroupModal from '../add-alarm-types-to-group-modal/AddAlarmTypesToGroupModal';
import { filtersAtom, pageAtom, pageSizeAtom } from '../state';
import AlarmTypeGroupExpandedRow from './AlarmTypeGroupExpandedRow';
import TableHeader from './TableHeader';
import FilterPanel from './filter-panel/FilterPanel';
import { columns, getSearchParams, getTableData } from './util';

export function AlarmTypeGroupsList() {
  const [filters, setFilters] = useAtom(filtersAtom);
  const [page, setPage] = useAtom(pageAtom);
  const pageSize = useAtomValue(pageSizeAtom);

  const [tableData, setTableData] = useState<TableData>({
    columns,
    rows: [],
  });

  const [allAlarmTypeGroups, setAllAlarmTypeGroups] = useState<
    AlarmTypeGroup[]
  >([]);

  const [total, setTotal] = useState(0);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [openAddAlarmTypeGroup, setOpenAddAlarmTypeGroup] = useState(false);
  const [openAddAlarmTypesToGroup, setOpenAddAlarmTypesToGroup] =
    useState(false);
  const [selectedAlarmTypeGroupId, setSelectedAlarmTypeGroupId] = useState<
    number | null
  >(null);

  const { isLoading, isError, error, data } = useAlarmTypeGroups(
    getSearchParams(page, filters, pageSize)
  );

  useEffect(() => {
    if (data) {
      setAllAlarmTypeGroups(data.items);
      setTotal(data.total);
      setTableData(getTableData(data.items, onClickAddAlarmType));
    }
  }, [data]);

  const onApplyFilters = (newFilters: AlarmTypeGroupFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const onClickAddAlarmType = (alarmTypeGroupId: number) => () => {
    setSelectedAlarmTypeGroupId(alarmTypeGroupId);
    setOpenAddAlarmTypesToGroup(true);
  };

  const fromIndex = (page - 1) * pageSize;
  const toIndex = Math.min(fromIndex + pageSize - 1, total - 1);

  const canAddAlarmTypeGroup = useCanAddAlarmTypeGroup();

  const tableHeader = (
    <TableHeader
      canAddAlarmTypeGroup={canAddAlarmTypeGroup}
      hasCustomFiltersApplied={filters.name.values.length > 0}
      isLoading={isLoading}
      onClickAddAlarmTypeGroup={() => setOpenAddAlarmTypeGroup(true)}
      onClickFilter={() => setOpenFilterPanel(true)}
    />
  );

  const tableFooter = (
    <div className="dark:bg-dark-bg border-t bg-white p-4 dark:border-gray-800">
      <Pagination
        from={fromIndex + 1}
        to={toIndex + 1}
        pageSize={pageSize}
        total={total}
        disabled={isLoading}
        onChangePage={(val) => {
          setPage(val);
        }}
      />
    </div>
  );

  const getExpandedRow = (rowId: string) => {
    const expandedAlarmTypeGroup = allAlarmTypeGroups.find(
      (group) => String(group.alarmTypeGroupId) === rowId
    );
    return expandedAlarmTypeGroup ? (
      <AlarmTypeGroupExpandedRow alarmTypeGroup={expandedAlarmTypeGroup} />
    ) : null;
  };

  return (
    <Page title="Alarm Type Groups" titleFontSize="large">
      {isError && (
        <div className="mb-2">
          <Alert type="error">{error.message}</Alert>
        </div>
      )}
      {openAddAlarmTypeGroup && (
        <AddEditAlarmTypeGroup
          onClose={() => setOpenAddAlarmTypeGroup(false)}
        />
      )}
      {openAddAlarmTypesToGroup && selectedAlarmTypeGroupId && (
        <AddAlarmTypesToGroupModal
          alarmTypeGroupId={selectedAlarmTypeGroupId}
          onClose={() => {
            setOpenAddAlarmTypesToGroup(false);
            setSelectedAlarmTypeGroupId(null);
          }}
        />
      )}
      <FilterPanel
        open={openFilterPanel}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClose={() => setOpenFilterPanel(false)}
      />
      <div className="dark:bg-dark-bg -mx-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden sm:-mx-6 md:mx-0 md:rounded-lg">
        <Table
          data={tableData}
          header={tableHeader}
          footer={tableFooter}
          loading={isLoading}
          renderExpandedRow={getExpandedRow}
        />
      </div>
    </Page>
  );
}

export default withErrorBoundary(AlarmTypeGroupsList);
